import React, {useEffect, useRef, useState} from 'react';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle,
  Clock,
  DiamondPercent,
  DollarSign,
  ShoppingCart,
  Users
} from 'lucide-react';
import BarChart from '../components/BarChart';
import axios from 'axios';
import {toast} from 'sonner';
import {useAuth} from '../Context/AuthContext';
import {DayPicker} from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {useNavigate} from 'react-router-dom';

// Lightweight UI helpers
const ToolbarActions = ({ children }) => (
  <div className="flex flex-wrap items-center gap-2 mt-4 md:mt-0 md:justify-end">{children}</div>
);

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
}

const Popover = ({ open, onOpenChange, children }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handleClickAway = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open, onOpenChange]);
  return (
    <div ref={containerRef} className="relative">{React.Children.map(children, child => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child, { open, onOpenChange });
    })}</div>
  );
};

const PopoverTrigger = ({ asChild, children, open, onOpenChange }) => (
  <div onClick={() => onOpenChange(!open)} className="cursor-pointer select-none">
    {children}
  </div>
);

const PopoverContent = ({ children, open }) => (
  open ? (
    <div className="absolute right-0 z-50 mt-2 w-[calc(100vw-2rem)] max-w-[640px] rounded-lg border border-gray-700 bg-gray-800 p-0 shadow-xl">
      {children}
    </div>
  ) : null
);

const Button = ({ id, variant = 'outline', className = '', onClick, children }) => (
  <button
    id={id}
    onClick={onClick}
    className={
      `inline-flex items-center gap-2 h-9 px-3 text-sm rounded-xl transition-colors ` +
      `${variant === 'outline'
        ? 'border border-gray-500/70 bg-gray-800/40 text-gray-200 hover:bg-gray-700/60 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/40'
        : 'bg-purple-600 text-white hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/40'} ` +
      className
    }
  >
    {children}
  </button>
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [loadingStartTime, setLoadingStartTime] = useState(Date.now());
  const { user, getToken } = useAuth();
const navigate = useNavigate();
  // Date range state for header filter
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState({ from: undefined, to: undefined });
  const [tempDateRange, setTempDateRange] = useState({ from: undefined, to: undefined });
  const formatYMD = (d) => (d ? new Date(d).toLocaleDateString('en-CA') : '');
  const handleDateRangeReset = () => {
    setTempDateRange({ from: undefined, to: undefined });
    setDate({ from: undefined, to: undefined });
    // Refetch without filters
    fetchDashboardData();
  };
  const handleDateRangeApply = () => {
    setDate(tempDateRange);
    setIsOpen(false);
    // Refetch with filters when both dates are present
    fetchDashboardData(
      tempDateRange?.from ? formatYMD(tempDateRange.from) : undefined,
      tempDateRange?.to ? formatYMD(tempDateRange.to) : undefined
    );
  };

  useEffect(() => {
    const fetchDashBoardData = async (from, to) => {
    try {
      setLoading(true);
      // setLoadingStartTime(Date.now());
      setError(null);

      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      // Build URL with optional date filters
      const baseUrl = 'https://apisellhub.com/api/super-admin/dashboard';
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status) {
        setDashboardData(response.data);
        // Ensure minimum loading time of 800ms to prevent flickering
        // const elapsedTime = Date.now() - loadingStartTime;
        // const minLoadingTime = 800;
        //
        // if (elapsedTime < minLoadingTime) {
        //   // setTimeout(() => {
        //   //   setLoading(false);
        //   // }, minLoadingTime - elapsedTime);
        // } else {
        //   setLoading(false);
        // }
      } else {
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    } catch (err) {
      console.error('Dashboard API error:', err);
      if (err.response?.status === 401) {
        navigate("/center/login", { replace: true });
        sessionStorage.removeItem("Market-token");
        toast.error('Token Expired. Please login again.');
        // setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to load dashboard data');
      }
      setLoading(false);
    } finally{
      setLoading(false);
    }
  };
    fetchDashBoardData().then();
  }, [getToken,navigate]);

  // getToken,loadingStartTime,navigate

  const fetchDashboardData = async (from, to) => {
    try {
      setLoading(true);
      // setLoadingStartTime(Date.now());
      setError(null);

      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      // Build URL with optional date filters
      const baseUrl = 'https://apisellhub.com/api/super-admin/dashboard';
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status) {
        setDashboardData(response.data);
        // Ensure minimum loading time of 800ms to prevent flickering
        // const elapsedTime = Date.now() - loadingStartTime;
        // const minLoadingTime = 800;
        //
        // if (elapsedTime < minLoadingTime) {
        //   setTimeout(() => {
        //     setLoading(false);
        //   }, minLoadingTime - elapsedTime);
        // } else {
        //   setLoading(false);
        // }
      } else {
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    } catch (err) {
      console.error('Dashboard API error:', err);
      if (err.response?.status === 401) {
        navigate("/center/login", { replace: true });
        sessionStorage.removeItem("Market-token");
        toast.error('Token Expired. Please login again.');
        // setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to load dashboard data');
      }
      setLoading(false);
    } finally{
      setLoading(false);
    }
  };

  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case 'active':
  //     case 'completed':
  //       return 'bg-green-100 text-green-800';
  //     case 'draft':
  //       return 'bg-gray-100 text-gray-800';
  //     case 'processing':
  //       return 'bg-blue-100 text-blue-800';
  //     case 'shipped':
  //       return 'bg-purple-100 text-purple-800';
  //     case 'pending':
  //       return 'bg-yellow-100 text-yellow-800';
  //     default:
  //       return 'bg-gray-100 text-gray-800';
  //   }
  // };

  // const getStatusIcon = (status) => {
  //   switch (status) {
  //     case 'completed':
  //       return <CheckCircle className="h-4 w-4" />;
  //     case 'processing':
  //       return <Package className="h-4 w-4" />;
  //     case 'shipped':
  //       return <Truck className="h-4 w-4" />;
  //     case 'pending':
  //       return <Clock className="h-4 w-4" />;
  //     default:
  //       return <AlertCircle className="h-4 w-4" />;
  //   }
  // };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Welcome Section Skeleton */}
        <div className="bg-gradient-to-r from-gray-800 to-yellow-600 rounded-lg p-6 text-white">
          <div className="h-8 bg-yellow-400/30 rounded w-64 mb-2 animate-pulse"></div>
          <div className="h-4 bg-yellow-400/30 rounded w-80 animate-pulse"></div>
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div className="w-full">
                  <div className="h-4 bg-gray-600 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-600 rounded w-20 mb-4 animate-pulse"></div>
                </div>
                <div className="w-12 h-12 bg-gray-600 rounded-full animate-pulse"></div>
              </div>
              <div className="flex items-center">
                <div className="h-4 bg-gray-600 rounded w-16 animate-pulse"></div>
                <div className="h-4 bg-gray-600 rounded w-24 ml-2 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Chart Skeleton */}
        <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6">
          <div className="h-6 bg-gray-600 rounded w-48 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-600 rounded w-64 mb-6 animate-pulse"></div>
          <div className="h-64 bg-gray-700 rounded animate-pulse"></div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6">
          <div className="h-6 bg-gray-600 rounded w-32 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex space-x-4">
                <div className="h-4 bg-gray-600 rounded w-20 animate-pulse"></div>
                <div className="h-4 bg-gray-600 rounded w-32 animate-pulse"></div>
                <div className="h-4 bg-gray-600 rounded w-24 animate-pulse"></div>
                <div className="h-4 bg-gray-600 rounded w-20 animate-pulse"></div>
                <div className="h-4 bg-gray-600 rounded w-16 animate-pulse"></div>
                <div className="h-4 bg-gray-600 rounded w-24 animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Loading indicator */}
        <div className="text-center py-8">
          <div className="inline-flex items-center space-x-2 text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            <span>Loading dashboard data...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-red-400 mb-2">Error Loading Dashboard</h3>
        <p className="text-red-300">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }


  // Prepare chart data from API response
  const chartData = dashboardData?.date_sales?.date?.map((date, index) => ({
    label: date,
    value: dashboardData.date_sales.data[index] || 0
  })) || [];

  const averageOrderValue = dashboardData?.total_sell_amount / dashboardData?.total_sell;

  // Updated stats with real data
  const stats = [
    {
      name: 'Total Sellers',
      value: `${dashboardData?.total_seller ||  0}`,
      change: '+0%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-green-500'
    },
    {
      name: 'Total Sales',
      // value: `$${Number(dashboardData?.total_sell ||  0).toFixed(2)}`,
      value: `${Number(dashboardData?.total_sell ||  0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      change: '+0%',
      changeType: 'positive',
      icon: ShoppingCart,
      color: 'bg-blue-500'
    },
    {
      name: 'Total Sales Amount',
      value: `$${Number(dashboardData?.total_sell_amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      change: '+0%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'bg-orange-500'
    },
    {
      name: 'Average Order Value',
      value: `${Number(averageOrderValue || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`,
      change: '+0%',
      changeType: 'positive',
      icon: DiamondPercent,
      color: 'bg-yellow-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-black via-gray-900 to-yellow-600 rounded-lg p-6 text-white shadow-lg">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
    <div>
      <h2 className="text-2xl font-bold mb-2 text-yellow-400">
        Welcome back, {user?.name || 'User'}! 👋
      </h2>
      <p className="text-gray-200">
        Here's what's happening with your store today.
      </p>
      {user?.email && (
        <p className="text-gray-400 text-sm mt-2">
          Logged in as: {user.email}
        </p>
      )}
    </div>

    <ToolbarActions>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button id="date" variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10">
            <CalendarDays size={16} className="text-yellow-400" />
            {date?.from ? (
              date.to ? (
                <>
                  {formatYMD(date.from)} - {formatYMD(date.to)}
                </>
              ) : (
                formatYMD(date.from)
              )
            ) : (
              <span className="text-gray-300">Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent open={isOpen} className="bg-black/90 border border-yellow-500">
          <div className="p-4">
            <DayPicker
              mode="range"
              selected={tempDateRange}
              onSelect={setTempDateRange}
              numberOfMonths={2}
              captionLayout="buttons"
              pagedNavigation
              className="rdp w-full"
              classNames={{
                root: 'text-gray-200',
                months: 'flex flex-col sm:flex-row gap-6 sm:gap-8',
                month: 'space-y-3',
                caption: 'flex items-center justify-between px-1',
                caption_label: 'text-sm font-semibold text-yellow-400',
                nav: 'flex items-center gap-2',
                button_previous: 'h-8 w-8 rounded-md border border-yellow-500 hover:bg-yellow-500/20',
                button_next: 'h-8 w-8 rounded-md border border-yellow-500 hover:bg-yellow-500/20',
                table: 'w-full border-collapse',
                head_row: 'grid grid-cols-7 text-xs text-gray-400',
                head_cell: 'h-8 w-8 grid place-items-center',
                row: 'grid grid-cols-7',
                cell: 'h-9 w-9 p-0 grid place-items-center relative',
                day: 'h-8 w-8 rounded-md hover:bg-yellow-500/20 transition-colors',
                day_selected: 'bg-yellow-500 text-black hover:bg-yellow-600',
                day_today: 'ring-1 ring-yellow-400',
                day_outside: 'text-gray-500 opacity-50',
                day_disabled: 'opacity-30 cursor-not-allowed',
                range_start: 'bg-yellow-500 text-black rounded-l-md',
                range_end: 'bg-yellow-500 text-black rounded-r-md',
                range_middle: 'bg-yellow-400/20 text-gray-100',
              }}
            />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-700 p-3 bg-black/70 rounded-b-lg">
            <Button variant="outline" onClick={handleDateRangeReset} className="h-9 px-4 border-yellow-500 text-yellow-400 hover:bg-yellow-500/10">
              Reset
            </Button>
            <Button onClick={handleDateRangeApply} className="h-9 px-4 bg-yellow-500 text-black hover:bg-yellow-600">
              Apply Filter
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Button variant="outline" onClick={handleDateRangeReset} className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10">
        Reset Filter
      </Button>
    </ToolbarActions>
  </div>
</div>


      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">{stat.name}</p>
                  <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              {/* <div className="mt-4 flex items-center">
                {stat.changeType === 'positive' ? (
                  <ArrowUpRight className="h-4 w-4 text-green-400" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-400" />
                )}
                <span className={`text-sm font-medium ml-1 ${
                  stat.changeType === 'positive' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-400 ml-1">from last month</span>
              </div> */}
            </div>
          );
        })}
      </div>

      {/* Revenue Chart */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6">
        <div className="overflow-x-auto -mx-2 md:mx-0">
          <div className="min-w-[720px] md:min-w-0 px-2 md:px-0">
            <BarChart
              data={chartData}
              title="Monthly Sales Trend"
              subtitle="Sales growth over the last 13 months"
              height={300}
              barColor="bg-gradient-to-t from-yellow-600 to-yellow-500"
              hoverColor="bg-yellow-700"
              showTrend={true}
              showValues={true}
              gridLines={true}
              animate={true}
            />
          </div>
        </div>
      </div>

      {/* Latest Sellers */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Latest Sellers</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Subdomain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Created Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {dashboardData?.latest_sellers?.map((seller) => (
                <tr key={seller.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{seller.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{seller.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{seller.subdomain}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{seller.country}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {seller.status === 1 ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-400" />
                      )}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        seller.status === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {seller.status === 1 ? 'Active' : 'Pending'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {timeAgo(seller.created_at)}
                      {/* {new Date(seller.created_at).toLocaleDateString()} */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
